import { getIdentityToken, useToken } from '@privy-io/react-auth';
import { useCreateWallet } from '@privy-io/react-auth/extended-chains';
import { useCallback, useMemo, useRef, useState } from 'react';

import { type WalletAccessResponse } from '@/api/wallet/wallet-access';

import { clearPrivySession } from './privy-session';
import { usePasskeySignIn } from './use-passkey-sign-in';
import { usePrivySession } from './use-privy-session';
import {
  accessWallet,
  describeWalletAccessFailure,
  type WalletAccessPorts,
} from './wallet-access-flow';
import { type WalletOperation } from './wallet-operation';
import { walletUnlockSession } from './wallet-unlock-session';

interface WalletAccessFlowOptions {
  gammaUserId: string | null;
  privyReady: boolean;
  lock: () => Promise<void>;
  readAccess: () => Promise<WalletAccessResponse>;
}

export function useWalletAccessFlow({
  gammaUserId,
  privyReady,
  lock,
  readAccess,
}: WalletAccessFlowOptions) {
  const ports = useWalletAccessPorts(readAccess);
  const [registeredOwners] = useState(() => new Set<string>());
  const { isBusy, error, run } = useSingleAccessAttempt(lock);

  const start = useCallback(
    (useExistingPasskey: boolean): Promise<boolean> => {
      if (!gammaUserId || !privyReady) {
        return Promise.resolve(false);
      }

      const operation = walletUnlockSession.beginAccess();
      const request = { gammaUserId, useExistingPasskey, registeredOwners, operation };
      return run(operation, () => accessWallet(ports, request));
    },
    [gammaUserId, ports, privyReady, registeredOwners, run],
  );

  const setUp = useCallback(() => start(false), [start]);
  const unlock = useCallback(() => start(true), [start]);
  return { setUp, unlock, isBusy, error };
}

function useSingleAccessAttempt(lock: () => Promise<void>) {
  const [isBusy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = useRef<Promise<boolean> | null>(null);

  const run = useCallback(
    (operation: WalletOperation, work: () => Promise<boolean>): Promise<boolean> => {
      if (pending.current) {
        return pending.current;
      }

      setBusy(true);
      setError(null);
      pending.current = work()
        .catch(async (failure: unknown) => {
          if (operation.isCurrent()) {
            setError(describeWalletAccessFailure(failure));
            await lock();
          }

          return false;
        })
        .finally(() => {
          pending.current = null;
          setBusy(false);
        });
      return pending.current;
    },
    [lock],
  );

  return { isBusy, error, run };
}

function useWalletAccessPorts(readAccess: () => Promise<WalletAccessResponse>): WalletAccessPorts {
  const session = usePrivySession();
  const { getAccessToken } = useToken();
  const { createWallet } = useCreateWallet();
  const signInWithPasskey = usePasskeySignIn();

  return useMemo(
    () => ({
      readAccess,
      clearPrivySession: () => clearPrivySession(session),
      signInWithPasskey,
      getAccessToken,
      getIdentityToken,
      createWallet: async () => (await createWallet({ chainType: 'sui' })).wallet,
    }),
    [createWallet, getAccessToken, readAccess, session, signInWithPasskey],
  );
}
